import { Card, CardContent } from "@/components/ui/card";
import { Star, MapPin, Calendar, Quote } from "lucide-react";
import { Link } from "react-router-dom";

interface TestimonialCardProps {
  name: string;
  country: string;
  date: string;
  rating: number;
  comment: string;
  compact?: boolean;
  variant?: 'default' | 'wall' | 'feature';
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getAvatarColor = (name: string) => {
  const colors = [
    'bg-roca-yellow/20 text-roca-yellow border-roca-yellow',
    'bg-roca-red/20 text-roca-red border-roca-red',
    'bg-primary/20 text-primary border-primary',
    'bg-accent/20 text-accent border-accent',
    'bg-roca-orange/20 text-roca-orange border-roca-orange',
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

export const TestimonialCard = ({ name, country, date, rating, comment, compact = false, variant = 'default' }: TestimonialCardProps) => {
  const starSize = variant === 'feature' ? 'h-5 w-5' : 'h-4 w-4';
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`${starSize} ${i < rating ? 'fill-roca-orange text-roca-orange' : 'text-muted-foreground'}`}
      />
    ));
  };

  const displayComment = compact && comment.length > 80
    ? comment.slice(0, 80) + '...'
    : comment;

  if (variant === 'wall' || variant === 'feature') {
    const isFeature = variant === 'feature';
    const cardEl = (
      <Card className={`group relative overflow-hidden border border-border/50 bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-float h-full ${isFeature ? 'shadow-float' : 'shadow-card'}`}>
        <Quote
          aria-hidden
          className={`absolute -top-2 -right-2 text-roca-orange/10 ${isFeature ? 'w-32 h-32' : 'w-20 h-20'} rotate-12 pointer-events-none`}
        />
        <CardContent className={isFeature ? 'p-8 md:p-10' : 'p-6'}>
          <div className="flex gap-1 mb-3">{renderStars(rating)}</div>
          <p className={`text-foreground/90 leading-relaxed ${isFeature ? 'text-lg md:text-xl font-light italic' : 'text-sm italic'}`}>
            "{displayComment}"
          </p>
          <div className={`flex items-center gap-3 mt-5 pt-4 border-t border-border/40`}>
            <div className={`rounded-full flex items-center justify-center font-bold border-2 flex-shrink-0 ${isFeature ? 'w-12 h-12 text-base' : 'w-10 h-10 text-sm'} ${getAvatarColor(name)}`}>
              {getInitials(name)}
            </div>
            <div className="min-w-0">
              <div className={`font-semibold text-foreground truncate ${isFeature ? 'text-base' : 'text-sm'}`}>{name}</div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{country}</span>
                <span className="opacity-50">•</span>
                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{date}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
    return cardEl;
  }

  const cardContent = (
    <Card className="shadow-card hover:shadow-float transition-smooth h-full border-l-4 border-primary/30">
      <CardContent className="p-6">
        {/* Header avec avatar et infos */}
        <div className="flex items-start gap-4 mb-4">
          {/* Avatar avec initiales */}
          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg border-2 flex-shrink-0 ${getAvatarColor(name)}`}>
            {getInitials(name)}
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Nom */}
            <h3 className="font-semibold text-lg text-foreground truncate">{name}</h3>
            
            {/* Localisation et date */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-1">
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span>{country}</span>
              </div>
              {!compact && (
                <>
                  <span>•</span>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{date}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Étoiles */}
        <div className="flex gap-1 mb-3">
          {renderStars(rating)}
        </div>
        
        {/* Commentaire */}
        <p className="text-sm text-muted-foreground leading-relaxed italic">
          "{displayComment}"
        </p>
      </CardContent>
    </Card>
  );

  if (compact) {
    return (
      <Link to="/avis" className="block hover:scale-[1.02] transition-transform">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
};